import { ChangeDetectionStrategy, Component, Input } from "@angular/core";
import { PermitType, PersonRole } from "@ha/data/basic";
import { HaModalService } from "@ha/ui/common";
import { UntilDestroy } from "@ngneat/until-destroy";
import { Select, Store } from "@ngxs/store";
import { Observable } from "rxjs";

import { Permit } from "../../../permit-create/models/permit.model";
import { PermitCreateSelectors } from "../../../permit-create/states/permit-create.selectors";
import { PermitDetailsSelectors } from "../../states/permit-details.selectors";
import {
    ExportPermitEmailModalComponent,
    ExportPermitEmailModalComponentData,
} from "../export-permit-email-modal/export-permit-email-modal.component";

@UntilDestroy()
@Component({
    selector: "pmt-export-permit",
    templateUrl: "./export-permit.component.html",
    styleUrls: ["./export-permit.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExportPermitComponent {
    @Input() public permitId: number;

    @Select(PermitDetailsSelectors.currentUserIsIssuer)
    public currentUserIsIssuer$: Observable<boolean>;

    @Select(PermitDetailsSelectors.currentUserIsSuperintendent)
    public currentUserIsSuperintendent$: Observable<boolean>;

    @Select(PermitCreateSelectors.permit)
    public permit$: Observable<Permit>;

    @Select(PermitDetailsSelectors.currentUserRoles)
    public currentUserRoles$: Observable<PersonRole[]>;

    public PersonRole = PersonRole;

    public waitingForDownload = false;
    public waitingForFlammableAtexDownload = false;
    public waitingForGasFreeDownload = false;

    public PermitType = PermitType;

    public get pdfUrl() {
        return `/api/Permit/GeneratePdf?permitId=${this.permitId}`;
    }

    public get flammableAtexPdfUrl() {
        return `/api/Permit/GenerateFlammableAtexPermitPdf?permitId=${this.permitId}`;
    }

    public get gasFreeDeclarationPdfUrl() {
        return `/api/Permit/GenerateGasFreeDeclarationPdf?permitId=${this.permitId}`;
    }

    constructor(private store: Store, private haModalService: HaModalService) {}

    public openExportPermitEmailModal(): void {
        this.haModalService.open(ExportPermitEmailModalComponent, true, <ExportPermitEmailModalComponentData>{ permitId: this.permitId });
    }

    public beginDownload(): void {
        this.waitingForDownload = true;
    }

    public endDownload(): void {
        this.waitingForDownload = false;
    }

    public beginFlammableAtexDownload(): void {
        this.waitingForFlammableAtexDownload = true;
    }

    public endFlammableAtexDownload(): void {
        this.waitingForFlammableAtexDownload = false;
    }

    public beginGasFreeDownload(): void {
        this.waitingForGasFreeDownload = true;
    }

    public endGasFreeDownload(): void {
        this.waitingForGasFreeDownload = false;
    }

    public hasFlammableAtexContext(permit: Permit | null | undefined): boolean {
        return !!permit && permit.flammableAtexPermitId != null;
    }

    public showGasFreeDeclaration(permit: Permit | null | undefined): boolean {
        return this.hasFlammableAtexContext(permit) && permit.flammableAtexHasMeasurements;
    }

    public canExportRegularPermit(
        currentUserIsIssuer: boolean | null | undefined,
        currentUserIsSuperintendent: boolean | null | undefined,
        currentUserRoles: PersonRole[] | null | undefined,
    ): boolean {
        if (currentUserIsIssuer || currentUserIsSuperintendent) {
            return true;
        }

        return currentUserRoles?.some((_) => _ === PersonRole.HotWorker || _ === PersonRole.FireWatcher) ?? false;
    }
}
